import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

const AuthContext = createContext(null);

// Configurable inactivity timeout (milliseconds). Default: 15 minutes.
export const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

// Activity events that reset the inactivity timer
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('dp_token'));
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [subOrganization, setSubOrganization] = useState(null);
  const [otpVerified, setOtpVerified] = useState(() => localStorage.getItem('dp_otp_verified') === 'true');
  const [loading, setLoading] = useState(false);
  const [businessCode, setBusinessCode] = useState(() => localStorage.getItem('dp_business_code') || '');
  const [merchants, setMerchants] = useState(() => {
    try { const saved = localStorage.getItem('dp_merchants'); return saved ? JSON.parse(saved) : []; } catch (e) { return []; }
  });
  const [businesses, setBusinesses] = useState(() => {
    try { const saved = localStorage.getItem('dp_businesses'); return saved ? JSON.parse(saved) : []; } catch (e) { return []; }
  });

  const inactivityTimerRef = useRef(null);
  const logoutRef = useRef(null);

  // Keep logoutRef in sync so the timer callback always calls the latest logout
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef?.current) {
      clearTimeout(inactivityTimerRef?.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      if (logoutRef?.current) logoutRef?.current();
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer]);

  useEffect(() => {
    if (token) {
      apiService?.setToken(token);
      const savedUser = localStorage.getItem('dp_user');
      const savedPermissions = localStorage.getItem('dp_permissions');
      const savedRole = localStorage.getItem('dp_role');
      const savedOrg = localStorage.getItem('dp_org');
      const savedSubOrg = localStorage.getItem('dp_sub_org');
      const savedBusinessCode = localStorage.getItem('dp_business_code');
      try { if (savedUser) setUser(JSON.parse(savedUser)); } catch (e) { localStorage.removeItem('dp_user'); }
      try { if (savedPermissions) setPermissions(JSON.parse(savedPermissions)); } catch (e) { localStorage.removeItem('dp_permissions'); }
      try { if (savedRole) setRole(JSON.parse(savedRole)); } catch (e) { localStorage.removeItem('dp_role'); }
      try { if (savedOrg) setOrganization(JSON.parse(savedOrg)); } catch (e) { localStorage.removeItem('dp_org'); }
      try { if (savedSubOrg) setSubOrganization(JSON.parse(savedSubOrg)); } catch (e) { localStorage.removeItem('dp_sub_org'); }
      if (savedBusinessCode) setBusinessCode(savedBusinessCode);
    }
  }, [token]);

  // Start/stop inactivity timer based on authentication state
  useEffect(() => {
    if (!token) {
      clearInactivityTimer();
      ACTIVITY_EVENTS?.forEach(event => window.removeEventListener(event, resetInactivityTimer));
      return;
    }

    // Start timer and listen for activity
    resetInactivityTimer();
    ACTIVITY_EVENTS?.forEach(event => window.addEventListener(event, resetInactivityTimer, { passive: true }));

    return () => {
      clearInactivityTimer();
      ACTIVITY_EVENTS?.forEach(event => window.removeEventListener(event, resetInactivityTimer));
    };
  }, [token, resetInactivityTimer, clearInactivityTimer]);

  const login = useCallback(async (credentials) => {
    try {
      const response = await apiService?.post('/auth/login', credentials);
      const data = response?.data;

      // Detect failure responses returned with 2xx status
      const responseMessage = data?.message || data?.detail || data?.error || '';
      const isFailureMessage = typeof responseMessage === 'string' &&
        /login failed|invalid|unauthorized|incorrect|failed|error/i?.test(responseMessage);
      const accessToken = data?.token_details?.access_token || data?.access_token || data?.token;

      if (isFailureMessage || !accessToken) {
        return { success: false, error: responseMessage || 'Invalid credentials. Please try again.' };
      }

      const userData = data?.user_details || data?.user;
      const userRole = data?.role;
      const userPermissions = data?.permissions || [];
      const org = data?.organization;
      const subOrg = data?.sub_organization;
      const bc = credentials?.organization_id || data?.business_code || userData?.business_code || org?.business_code || '';
      const merchantsList = data?.merchants || [];
      const businessesList = data?.businesses || [];

      setToken(accessToken);
      setUser(userData);
      setRole(userRole);
      setPermissions(userPermissions);
      setOrganization(org);
      setSubOrganization(subOrg);
      setBusinessCode(bc);
      setMerchants(merchantsList);
      setBusinesses(businessesList);

      localStorage.setItem('dp_token', accessToken);
      localStorage.setItem('dp_user', JSON.stringify(userData));
      localStorage.setItem('dp_permissions', JSON.stringify(userPermissions));
      localStorage.setItem('dp_role', JSON.stringify(userRole));
      localStorage.setItem('dp_org', JSON.stringify(org));
      localStorage.setItem('dp_sub_org', JSON.stringify(subOrg));
      if (bc) localStorage.setItem('dp_business_code', bc);
      localStorage.setItem('dp_merchants', JSON.stringify(merchantsList));
      localStorage.setItem('dp_businesses', JSON.stringify(businessesList));

      apiService?.setToken(accessToken);
      return { success: true, data };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        return { success: false, error: 'Invalid credentials. Please try again.' };
      }
      const errMsg = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.error || 'Login failed. Please try again.';
      const statusDescription = error?.response?.data?.status_description;
      return { success: false, error: errMsg, status_description: statusDescription };
    }
  }, []);

  const logout = useCallback(() => {
    clearInactivityTimer();
    setUser(null);
    setToken(null);
    setPermissions([]);
    setRole(null);
    setOrganization(null);
    setSubOrganization(null);
    setOtpVerified(false);
    setBusinessCode('');
    setMerchants([]);
    setBusinesses([]);
    localStorage.removeItem('dp_token');
    localStorage.removeItem('dp_user');
    localStorage.removeItem('dp_permissions');
    localStorage.removeItem('dp_role');
    localStorage.removeItem('dp_org');
    localStorage.removeItem('dp_sub_org');
    localStorage.removeItem('dp_otp_verified');
    localStorage.removeItem('dp_business_code');
    localStorage.removeItem('dp_merchants');
    localStorage.removeItem('dp_businesses');
    apiService?.setToken(null);
  }, [clearInactivityTimer]);

  // Keep ref in sync with latest logout
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const setOtpVerifiedState = useCallback((val) => {
    setOtpVerified(val);
    localStorage.setItem('dp_otp_verified', val ? 'true' : 'false');
  }, []);

  const hasPermission = useCallback((route, action) => {
    return permissions?.some(p => p?.route === route && p?.action === action);
  }, [permissions]);

  const hasAnyPermission = useCallback((routes) => {
    return routes?.some(route => permissions?.some(p => p?.route === route));
  }, [permissions]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      permissions,
      role,
      organization,
      subOrganization,
      otpVerified,
      loading,
      businessCode,
      merchants,
      businesses,
      login,
      logout,
      setOtpVerifiedState,
      hasPermission,
      hasAnyPermission,
      isAuthenticated: !!token,
      inactivityTimeoutMs: INACTIVITY_TIMEOUT_MS,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
